/**
 * Step 8: Meet the Team
 *
 * Collects owner and team member information (name, title, bio, photo)
 * to build trust and credibility on the website.
 */

import { useState, useRef, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Plus, Trash2, Users, Upload, User } from 'lucide-react';
import type { StepTeamData, TeamMember } from '../../../types/onboarding';

interface Step8TeamProps {
  data: StepTeamData | null;
  onChange: (data: StepTeamData) => void;
  onNext: () => void;
  onBack: () => void;
  businessName: string;
  saving: boolean;
  onUploadImage?: (file: File, slot: string) => Promise<string>;
}

// Generate a unique ID for team members
const generateId = () => `team_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Create default owner entry
const createOwnerMember = (): TeamMember => ({
  id: generateId(),
  name: '',
  title: 'Owner',
  bio: '',
  photo_url: undefined,
  is_owner: true,
  display_order: 0,
});

// Create default team member entry
const createTeamMember = (order: number): TeamMember => ({
  id: generateId(),
  name: '',
  title: '',
  bio: '',
  photo_url: undefined,
  is_owner: false,
  display_order: order,
});

export function Step8Team({
  data,
  onChange,
  onNext,
  onBack,
  businessName,
  saving,
  onUploadImage,
}: Step8TeamProps) {
  // Initialize with existing data or a default owner entry
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(() => {
    if (data?.team_members && data.team_members.length > 0) {
      return data.team_members;
    }
    return [createOwnerMember()];
  });
  const [showTeamSection, setShowTeamSection] = useState(data?.show_team_section ?? true);
  const [uploading, setUploading] = useState<string | null>(null);

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Save current state to parent
  const saveData = () => {
    onChange({
      team_members: teamMembers.filter(m => m.name.trim()),
      show_team_section: showTeamSection,
    });
  };

  // Update a team member
  const updateMember = (id: string, updates: Partial<TeamMember>) => {
    setTeamMembers(members =>
      members.map(m => (m.id === id ? { ...m, ...updates } : m))
    );
  };

  // Add a new team member
  const addMember = () => {
    const maxOrder = Math.max(...teamMembers.map(m => m.display_order), 0);
    setTeamMembers([...teamMembers, createTeamMember(maxOrder + 1)]);
  };

  // Remove a team member
  const removeMember = (id: string) => {
    const member = teamMembers.find(m => m.id === id);
    // Don't allow removing the owner if it's the only member
    if (member?.is_owner && teamMembers.length === 1) return;
    setTeamMembers(members => members.filter(m => m.id !== id));
    saveData();
  };

  // Handle photo upload
  const handlePhotoUpload = async (file: File, memberId: string) => {
    if (!onUploadImage) return;

    setUploading(memberId);
    try {
      const url = await onUploadImage(file, `team_${memberId}`);
      updateMember(memberId, { photo_url: url });
      saveData();
    } catch (error) {
      console.error('Error uploading team photo:', error);
    } finally {
      setUploading(null);
    }
  };

  // Remove photo from member
  const removePhoto = (memberId: string) => {
    updateMember(memberId, { photo_url: undefined });
    saveData();
  };

  // Get owner member (first one marked as owner, or first member)
  const ownerMember = teamMembers.find(m => m.is_owner) || teamMembers[0];
  const otherMembers = teamMembers.filter(m => m.id !== ownerMember?.id);

  // Can always proceed (team section is optional)
  const canProceed = true;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Meet the Team
        </h1>
        <p className="text-gray-600">
          Introduce the people behind {businessName}. This builds trust and connection with customers.
        </p>
      </div>

      {/* Show team section toggle */}
      <div className="bg-gray-50 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={showTeamSection}
            onChange={(e) => {
              setShowTeamSection(e.target.checked);
              saveData();
            }}
            className="w-5 h-5 mt-0.5 text-primary-600 rounded"
          />
          <div>
            <span className="font-medium text-gray-900">
              Include a "Meet the Team" section on my website
            </span>
            <p className="text-sm text-gray-500 mt-1">
              Showing the faces behind your business builds trust and makes you more relatable.
            </p>
          </div>
        </label>
      </div>

      {showTeamSection && (
        <>
          {/* Owner/Primary Contact */}
          {ownerMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Owner / Primary Contact</h2>
              </div>
              <p className="text-sm text-gray-500">
                This person will be featured prominently on your website.
              </p>

              <TeamMemberCard
                member={ownerMember}
                onUpdate={(updates) => updateMember(ownerMember.id, updates)}
                onSave={saveData}
                onRemove={teamMembers.length > 1 ? () => removeMember(ownerMember.id) : undefined}
                onUploadPhoto={(file) => handlePhotoUpload(file, ownerMember.id)}
                onRemovePhoto={() => removePhoto(ownerMember.id)}
                uploading={uploading === ownerMember.id}
                fileInputRef={(el) => { fileInputRefs.current[ownerMember.id] = el; }}
                isOwner
              />
            </div>
          )}

          {/* Other Team Members */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <span className="text-sm text-gray-400">(Optional)</span>
            </div>
            <p className="text-sm text-gray-500">
              Add key team members that customers should know about.
            </p>

            <div className="space-y-4">
              {otherMembers.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onUpdate={(updates) => updateMember(member.id, updates)}
                  onSave={saveData}
                  onRemove={() => removeMember(member.id)}
                  onUploadPhoto={(file) => handlePhotoUpload(file, member.id)}
                  onRemovePhoto={() => removePhoto(member.id)}
                  uploading={uploading === member.id}
                  fileInputRef={(el) => { fileInputRefs.current[member.id] = el; }}
                />
              ))}

              {teamMembers.length < 6 && (
                <button
                  onClick={addMember}
                  className="flex items-center gap-2 px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add team member
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={() => {
            saveData();
            onNext();
          }}
          disabled={!canProceed || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Reusable team member card component
interface TeamMemberCardProps {
  member: TeamMember;
  onUpdate: (updates: Partial<TeamMember>) => void;
  onSave: () => void;
  onRemove?: () => void;
  onUploadPhoto: (file: File) => void;
  onRemovePhoto: () => void;
  uploading: boolean;
  fileInputRef: (el: HTMLInputElement | null) => void;
  isOwner?: boolean;
}

function TeamMemberCard({
  member,
  onUpdate,
  onSave,
  onRemove,
  onUploadPhoto,
  onRemovePhoto,
  uploading,
  fileInputRef,
  isOwner,
}: TeamMemberCardProps) {
  // Local ref to access the file input for clicking
  const localInputRef = useRef<HTMLInputElement | null>(null);

  // Combine the callback ref with our local ref
  const setInputRef = useCallback((el: HTMLInputElement | null) => {
    localInputRef.current = el;
    fileInputRef(el);
  }, [fileInputRef]);

  const handlePhotoClick = () => {
    localInputRef.current?.click();
  };

  return (
    <div className="bg-white border border-gray-300 rounded-xl p-4">
      <div className="flex gap-4">
        {/* Photo upload */}
        <div className="flex-shrink-0">
          <input
            ref={setInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadPhoto(file);
            }}
          />

          <div
            className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-all overflow-hidden relative"
            onClick={handlePhotoClick}
          >
            {uploading ? (
              <div className="animate-pulse text-primary-500">
                <Upload className="w-6 h-6" />
              </div>
            ) : member.photo_url ? (
              <>
                <img
                  src={member.photo_url}
                  alt={member.name || 'Team member'}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemovePhoto();
                  }}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            ) : (
              <div className="text-center p-2">
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                <span className="text-xs text-gray-500">Photo</span>
              </div>
            )}
          </div>
        </div>

        {/* Info fields */}
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name {isOwner && '*'}
              </label>
              <input
                type="text"
                value={member.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                onBlur={onSave}
                placeholder={isOwner ? 'Your name' : 'Team member name'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title / Role
              </label>
              <input
                type="text"
                value={member.title}
                onChange={(e) => onUpdate({ title: e.target.value })}
                onBlur={onSave}
                placeholder={isOwner ? 'e.g., Owner, Founder' : 'e.g., Head Chef, Lead Stylist'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Short Bio
              <span className="text-gray-400 font-normal ml-2">(Optional)</span>
            </label>
            <textarea
              value={member.bio || ''}
              onChange={(e) => onUpdate({ bio: e.target.value })}
              onBlur={onSave}
              rows={2}
              maxLength={200}
              placeholder={
                isOwner
                  ? 'e.g., I started this business 10 years ago with a passion for helping families...'
                  : 'e.g., With 5 years of experience, Maria specializes in...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
            <div className="flex justify-end text-xs text-gray-400 mt-1">
              {(member.bio || '').length}/200
            </div>
          </div>
        </div>

        {/* Remove button */}
        {onRemove && (
          <button
            onClick={onRemove}
            className="self-start p-2 text-gray-400 hover:text-red-500 transition-colors"
            title="Remove team member"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
